import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  type ChartDataset,
  type ChartOptions,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import './CostAnalysis.css';
import { UserSession, normalizeSessions, toMs } from '../utils/sessionUtils';
import { apiClient, PLAYGROUND, PLATFORM } from '../services/apiClient';
import { Auth, cookieUtils } from '../utils';
import { getUserDetailsById } from '../services/user';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend);

const CONCEPT_COLOR_PALETTE = [
  { border: '#2563eb', fill: 'rgba(37, 99, 235, 0.15)' },
  { border: '#16a34a', fill: 'rgba(22, 163, 74, 0.15)' },
  { border: '#dc2626', fill: 'rgba(220, 38, 38, 0.15)' },
  { border: '#9333ea', fill: 'rgba(147, 51, 234, 0.15)' },
  { border: '#f97316', fill: 'rgba(249, 115, 22, 0.15)' },
  { border: '#0ea5e9', fill: 'rgba(14, 165, 233, 0.15)' },
  { border: '#c026d3', fill: 'rgba(192, 38, 211, 0.15)' },
  { border: '#475569', fill: 'rgba(71, 85, 105, 0.15)' },
] as const;

type BasicUser = {
  _id: string;
  name?: string;
  email?: string;
  role?: string;
  grade?: string;
  entityState?: string;
  createdAt?: number | string;
};

type ConceptTag = {
  topicId?: string;
  topicName: string;
  depthRequired: number | null;
};

type QuestionConceptsState = {
  [questionId: string]: {
    status: 'loading' | 'success' | 'error';
    concepts: ConceptTag[];
    error?: string;
  };
};

type ConceptPerformancePoint = {
  weekStart: number;
  weekLabel: string;
  averageGrade: number;
  attemptCount: number;
};

type ConceptPerformanceDataset = {
  conceptKey: string;
  conceptLabel: string;
  firstSeenAt: number | null;
  points: ConceptPerformancePoint[];
};

type ConceptPerformanceSummary = {
  options: Array<{ value: string; label: string; firstSeenAt: number | null }>;
  datasets: Record<string, ConceptPerformanceDataset>;
};

type RefreshTone = 'neutral' | 'success' | 'error';

type RefreshStatus = {
  message: string;
  tone: RefreshTone;
};

const BASE_PATH = (() => {
  const raw = (process.env as any).PUBLIC_URL || '/';
  try {
    const u = new URL(raw, window.location.origin);
    return u.pathname.replace(/\/$/, '');
  } catch {
    return raw.replace(/\/$/, '');
  }
})();

const STORAGE_BUCKET_NAME = 'diraq-contents';

const isValidEnvironment = (value: string | null): value is 'local' | 'staging' | 'production' => {
  if (!value) return false;
  return value === 'local' || value === 'staging' || value === 'production';
};

const formatTimestamp = (ms?: number) => {
  if (typeof ms !== 'number' || !Number.isFinite(ms) || ms <= 0) return '—';
  try {
    return new Date(ms).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (_) {
    return new Date(ms).toLocaleString();
  }
};

type ConceptGradeLookup = {
  byId: Map<string, number | string | null | undefined>;
  byName: Map<string, number | string | null | undefined>;
};

const buildConceptGradeLookup = (grades: any): ConceptGradeLookup => {
  const lookup: ConceptGradeLookup = {
    byId: new Map(),
    byName: new Map(),
  };
  if (!Array.isArray(grades)) {
    return lookup;
  }
  grades.forEach((entry) => {
    if (!entry) return;
    const rawId =
      typeof entry.conceptId === 'string'
        ? entry.conceptId
        : entry.conceptId != null
          ? String(entry.conceptId)
          : '';
    const conceptId = rawId.trim();
    if (conceptId) {
      lookup.byId.set(conceptId, entry.grade);
    }
    const conceptName =
      typeof entry.conceptName === 'string' ? entry.conceptName.trim().toLowerCase() : '';
    if (conceptName) {
      lookup.byName.set(conceptName, entry.grade);
    }
  });
  return lookup;
};

const buildConceptKey = (concept: ConceptTag) => {
  const trimmedId = concept.topicId?.trim();
  if (trimmedId) return trimmedId;
  return concept.topicName.trim().toLowerCase();
};

const getConceptGradeValue = (concept: ConceptTag, lookup: ConceptGradeLookup) => {
  if (!concept) return undefined;
  const trimmedId = concept.topicId?.trim();
  const normalizedName = typeof concept.topicName === 'string' ? concept.topicName.trim().toLowerCase() : '';
  return (
    (trimmedId && lookup.byId.get(trimmedId)) ||
    (normalizedName && lookup.byName.get(normalizedName))
  );
};

const formatConceptGrade = (gradeValue: unknown) => {
  if (typeof gradeValue === 'number' && Number.isFinite(gradeValue)) return String(gradeValue);
  if (typeof gradeValue === 'string' && gradeValue.trim()) return gradeValue.trim();
  return '-';
};

const parseGradeToNumber = (gradeValue: unknown): number | null => {
  if (typeof gradeValue === 'number' && Number.isFinite(gradeValue)) {
    return gradeValue;
  }
  if (typeof gradeValue === 'string') {
    const trimmed = gradeValue.trim();
    if (!trimmed) return null;
    const parsed = Number(trimmed);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return null;
};

const getWeekStartTimestamp = (timestamp: number) => {
  const date = new Date(timestamp);
  date.setHours(0, 0, 0, 0);
  const day = date.getDay();
  const offset = (day + 6) % 7; // Monday as start of week
  date.setDate(date.getDate() - offset);
  return date.getTime();
};

const formatWeekRangeLabel = (weekStart: number) => {
  const start = new Date(weekStart);
  const end = new Date(weekStart);
  end.setDate(end.getDate() + 6);
  const startLabel = start.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  const endLabel = end.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  return `${startLabel} – ${endLabel}`;
};

const extractFileNameFromUrl = (url: string): string | null => {
  if (!url) return null;
  try {
    const baseOrigin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
    const urlObj = new URL(url, baseOrigin);
    const withoutQuery =
      urlObj.origin === 'null'
        ? url.split('?')[0]
        : `${urlObj.origin}${urlObj.pathname}`;
    const trimmed = withoutQuery.split('?')[0];
    const marker = `${STORAGE_BUCKET_NAME}/`;
    const markerIndex = trimmed.indexOf(marker);
    if (markerIndex === -1) return null;
    const startIndex = markerIndex + marker.length;
    const fileName = trimmed.slice(startIndex);
    return fileName ? decodeURIComponent(fileName) : null;
  } catch (_) {
    const cleaned = url.split('?')[0];
    const marker = `${STORAGE_BUCKET_NAME}/`;
    const markerIndex = cleaned.indexOf(marker);
    if (markerIndex === -1) return null;
    const startIndex = markerIndex + marker.length;
    const raw = cleaned.slice(startIndex);
    return raw ? decodeURIComponent(raw) : null;
  }
};

const getDownloadUrlFromPlatform = async (fileName: string, authToken: string): Promise<string | null> => {
  if (!fileName || !authToken) {
    return null;
  }
  try {
    const data = await apiClient.post<{ data?: { downloadUrl?: string } }>(
      '/file/download-url',
      PLATFORM,
      { fileName, bucketName: STORAGE_BUCKET_NAME }
    );
    return data?.data?.downloadUrl || null;
  } catch (error) {
    console.error('[UserAttemptsPage] download-url request error', error);
    return null;
  }
};

const formatAttemptDateForCsv = (timestamp?: number) => {
  if (typeof timestamp !== 'number' || !Number.isFinite(timestamp)) {
    return '';
  }
  try {
    return new Date(timestamp).toISOString();
  } catch {
    return '';
  }
};

const toCsvCell = (value: string) => {
  const safe = value.replace(/"/g, '""');
  return `"${safe}"`;
};

const UserAttemptsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('userId')?.trim() || '';
  const envFromParams = searchParams.get('env');
  const initialEnv = useMemo<'local' | 'staging' | 'production'>(() => {
    if (isValidEnvironment(envFromParams)) return envFromParams;
    const stored = sessionStorage.getItem('fermi-environment');
    if (isValidEnvironment(stored)) {
      return stored;
    }
    return 'production';
  }, [envFromParams]);
  const [environment, setEnvironment] = useState<'local' | 'staging' | 'production'>(initialEnv);
  const [user, setUser] = useState<BasicUser | null>(null);
  const [sessions, setSessions] = useState<UserSession[]>([]);
  const [userLoading, setUserLoading] = useState(false);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [userError, setUserError] = useState<string | null>(null);
  const [sessionsError, setSessionsError] = useState<string | null>(null);
  const [questionConcepts, setQuestionConcepts] = useState<QuestionConceptsState>({});
  const [selectedConcepts, setSelectedConcepts] = useState<string[]>([]);
  const [isRefreshingImages, setIsRefreshingImages] = useState(false);
  const [imageRefreshStatus, setImageRefreshStatus] = useState<RefreshStatus | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [zoomedImage, setZoomedImage] = useState<{ url: string; x: number; y: number } | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalAttempts, setTotalAttempts] = useState(0);
  const PAGE_SIZE = 20;

  useEffect(() => {
    const param = searchParams.get('env');
    if (isValidEnvironment(param) && param !== environment) {
      setEnvironment(param);
    }
  }, [searchParams, environment]);

  useEffect(() => {
    sessionStorage.setItem('fermi-environment', environment);
  }, [environment]);


  const loadUserDetails = useCallback(async () => {
    if (!userId) {
      setUser(null);
      return;
    }
    setUserLoading(true);
    setUserError(null);
    try {
      const details = await getUserDetailsById(userId);
      if (details) {
        setUser({
          _id: details._id,
          name: details.name,
          email: details.email,
          role: details.role,
          grade: details.grade,
        });
      } else {
        setUser(null);
        setUserError('User not found');
      }
    } catch (err: any) {
      setUser(null);
      setUserError(err?.message || 'Failed to load user details');
    } finally {
      setUserLoading(false);
    }
  }, [userId]);

  const loadUserSessions = useCallback(async (page = currentPage) => {
    if (!userId) {
      setSessions([]);
      return;
    }
    setSessionsLoading(true);
    setSessionsError(null);
    try {
      const data = await apiClient.get<any>(
        `/api/users/${userId}/attempts/details?page=${page}&limit=${PAGE_SIZE}`,
        PLAYGROUND,
      );
      const attemptsPayload = Array.isArray(data?.attempts) ? data.attempts : data;
      setSessions(normalizeSessions(attemptsPayload));
      if (typeof data?.total === 'number') {
        setTotalAttempts(data.total);
      }
    } catch (err: any) {
      setSessions([]);
      setSessionsError(err?.message || 'Failed to load attempts');
    } finally {
      setSessionsLoading(false);
    }
  }, [userId, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
    setTotalAttempts(0);
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    loadUserDetails();
    loadUserSessions(currentPage);
  }, [userId, currentPage, loadUserDetails, loadUserSessions]);

  const handleBackClick = useCallback(() => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/', { replace: true });
    }
  }, [navigate]);

  const handleEnvironmentChange = useCallback((nextEnv: 'local' | 'staging' | 'production') => {
    if (environment === nextEnv) return;
    setEnvironment(nextEnv);
    try {
      sessionStorage.setItem('fermi-environment', nextEnv);
    } catch (_) {
      // ignore storage errors
    }
    const params = new URLSearchParams(window.location.search);
    if (userId) params.set('userId', userId);
    params.set('env', nextEnv);
    navigate(`/user/attempts?${params.toString()}`, { replace: true });
  }, [environment, navigate, userId]);

  const openSessionLogs = useCallback((attemptId: string) => {
    const path = `${BASE_PATH}/session/${attemptId}`;
    window.open(path, '_blank');
  }, []);

  const handleRefreshSessions = useCallback(() => {
    setCurrentPage(1);
    loadUserSessions(1);
  }, [loadUserSessions]);

  const handleRefreshAttemptImages = useCallback(async () => {
    if (!sessions.length) {
      setImageRefreshStatus({ message: 'No attempts loaded yet.', tone: 'neutral' });
      return;
    }
    const token = cookieUtils.get(Auth.TOKEN) || '';
    if (!token) {
      setImageRefreshStatus({ message: 'Auth token missing; please log in again.', tone: 'error' });
      return;
    }

    const seen = new Set<string>();
    const fileNames: string[] = [];
    sessions.forEach((session) => {
      (['questionUrl', 'solutionUrl'] as const).forEach((field) => {
        const current = session[field];
        if (!current) return;
        const fileName = extractFileNameFromUrl(current);
        if (!fileName || seen.has(fileName)) return;
        seen.add(fileName);
        fileNames.push(fileName);
      });
    });

    if (!fileNames.length) {
      setImageRefreshStatus({ message: 'No refreshable images found.', tone: 'neutral' });
      return;
    }

    setIsRefreshingImages(true);
    setImageRefreshStatus({ message: 'Refreshing images...', tone: 'neutral' });

    try {
      const results = new Map<string, string>();
      const failures: string[] = [];
      await Promise.all(
        fileNames.map(async (fileName) => {
          const newUrl = await getDownloadUrlFromPlatform(fileName, token);
          if (newUrl) {
            results.set(fileName, newUrl);
          } else {
            failures.push(fileName);
          }
        }),
      );

      if (results.size) {
        setSessions((prevSessions) =>
          prevSessions.map((session) => {
            let updatedSession: UserSession | null = null;
            (['questionUrl', 'solutionUrl'] as const).forEach((field) => {
              const currentUrl = session[field];
              if (!currentUrl) return;
              const fileName = extractFileNameFromUrl(currentUrl);
              if (!fileName) return;
              const freshUrl = results.get(fileName);
              if (freshUrl && freshUrl !== currentUrl) {
                if (!updatedSession) {
                  updatedSession = { ...session };
                }
                updatedSession[field] = freshUrl;
              }
            });
            return updatedSession || session;
          }),
        );
      }

      if (results.size === 0) {
        setImageRefreshStatus({ message: 'Failed to refresh images.', tone: 'error' });
      } else if (failures.length) {
        setImageRefreshStatus({ message: 'Images refreshed with some failures.', tone: 'error' });
      } else {
        setImageRefreshStatus({ message: 'Images refreshed.', tone: 'success' });
      }
    } catch (error) {
      console.error('[UserAttemptsPage] refresh images error', error);
      setImageRefreshStatus({ message: 'Failed to refresh images.', tone: 'error' });
    } finally {
      setIsRefreshingImages(false);
    }
  }, [sessions]);

  const handleExportCsv = useCallback(() => {
    if (!sessions.length) {
      window.alert('No attempts available to export yet.');
      return;
    }
    const rows: string[][] = [];
    sessions.forEach((session) => {
      const trimmedQuestionId = session.questionId?.trim();
      if (!trimmedQuestionId) return;
      const conceptState = questionConcepts[trimmedQuestionId];
      if (!conceptState || conceptState.status !== 'success' || !conceptState.concepts?.length) {
        return;
      }
      const gradeLookup = buildConceptGradeLookup(session?.attemptDetails?.fermiAnalysis?.conceptGrades);
      conceptState.concepts.forEach((concept) => {
        const gradeValue = getConceptGradeValue(concept, gradeLookup);
        rows.push([
          session.attemptId,
          formatAttemptDateForCsv(session.attemptCreatedAt),
          session.questionUrl || '',
          session.solutionUrl || '',
          concept.topicName,
          formatConceptGrade(gradeValue),
          typeof concept.depthRequired === 'number' ? String(concept.depthRequired) : '',
        ]);
      });
    });
    if (!rows.length) {
      window.alert('Concept tags or grades are not available yet. Please try again after they finish loading.');
      return;
    }
    const header = [
      'Attempt ID',
      'Attempt Date',
      'Question URL',
      'Solution URL',
      'Concept',
      'Concept Grade',
      'Concept Depth',
    ];
    const csvLines = [header, ...rows].map((columns) =>
      columns
        .map((value) => toCsvCell((value ?? '').toString()))
        .join(','),
    );
    const csvContent = csvLines.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    link.download = `user-${userId || 'attempts'}-${timestamp}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [sessions, questionConcepts, userId]);

  const handleConceptSelectChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    const { options } = event.target;
    const values: string[] = [];
    for (let i = 0; i < options.length; i += 1) {
      if (options[i].selected) {
        values.push(options[i].value);
      }
    }
    setSelectedConcepts(values);
  }, []);

  const fetchQuestionConcepts = useCallback(async (questionId: string) => {
    if (!questionId) return;
    setQuestionConcepts((prev) => ({
      ...prev,
      [questionId]: { status: 'loading', concepts: [] },
    }));
    try {
      const data = await apiClient.get<{ concepts?: any[] }>(`/api/questions/${encodeURIComponent(questionId)}/concepts`, PLAYGROUND);
      const rawConcepts = Array.isArray(data?.concepts) ? data.concepts : [];
      const normalizedConcepts: ConceptTag[] = rawConcepts
        .map((entry: any) => {
          if (!entry) return null;
          const topicName = typeof entry.topicName === 'string' ? entry.topicName.trim() : '';
          if (!topicName) return null;
          const depthRaw = entry.depthRequired;
          const depthNumber = depthRaw == null ? null : Number(depthRaw);
          const depthRequired = Number.isFinite(depthNumber) ? depthNumber : null;
          const normalizedTopicId = typeof entry.topicId === 'string' ? entry.topicId.trim() : '';
          return {
            topicId: normalizedTopicId || undefined,
            topicName,
            depthRequired,
          } as ConceptTag;
        })
        .filter((entry: ConceptTag | null): entry is ConceptTag => entry !== null);
      setQuestionConcepts((prev) => ({
        ...prev,
        [questionId]: { status: 'success', concepts: normalizedConcepts },
      }));
    } catch (err: any) {
      // Handle 404 as success with empty concepts
      if ((err as any).status === 404) {
        setQuestionConcepts((prev) => ({
          ...prev,
          [questionId]: { status: 'success', concepts: [] },
        }));
        return;
      }
      setQuestionConcepts((prev) => ({
        ...prev,
        [questionId]: {
          status: 'error',
          concepts: [],
          error: err?.message || 'Failed to load concept tags',
        },
      }));
    }
  }, []);

  useEffect(() => {
    if (!sessions.length) return;
    const missingQuestionIds = new Set<string>();
    for (const session of sessions) {
      const trimmedId = session.questionId?.trim();
      if (trimmedId && !questionConcepts[trimmedId]) {
        missingQuestionIds.add(trimmedId);
      }
    }
    missingQuestionIds.forEach((id) => {
      fetchQuestionConcepts(id);
    });
  }, [sessions, questionConcepts, fetchQuestionConcepts]);

  useEffect(() => {
    setQuestionConcepts((prev) => {
      if (!prev || Object.keys(prev).length === 0) return prev;
      return {};
    });
  }, [environment, userId]);

  const conceptPerformance = useMemo<ConceptPerformanceSummary>(() => {
    if (!sessions.length) {
      return { options: [], datasets: {} };
    }

    type Bucket = { sum: number; count: number };
    type AggregateEntry = { label: string; firstSeenAt: number | null; buckets: Map<number, Bucket> };
    const aggregates = new Map<string, AggregateEntry>();

    sessions.forEach((session) => {
      const attemptTime = toMs(session.attemptCreatedAt);
      if (typeof attemptTime !== 'number' || !Number.isFinite(attemptTime)) {
        return;
      }
      const trimmedQuestionId = session.questionId?.trim();
      if (!trimmedQuestionId) return;
      const conceptState = questionConcepts[trimmedQuestionId];
      if (!conceptState || conceptState.status !== 'success' || !conceptState.concepts?.length) {
        return;
      }

      const gradeLookup = buildConceptGradeLookup(session?.attemptDetails?.fermiAnalysis?.conceptGrades);

      conceptState.concepts.forEach((concept) => {
        const conceptKey = buildConceptKey(concept);
        if (!conceptKey) return;
        const gradeValue = getConceptGradeValue(concept, gradeLookup);
        const numericGrade = parseGradeToNumber(gradeValue);
        if (numericGrade == null) return;
        const weekStart = getWeekStartTimestamp(attemptTime);
        if (!Number.isFinite(weekStart)) return;

        const label = concept.topicName || concept.topicId || 'Unnamed concept';
        const existing = aggregates.get(conceptKey) || {
          label,
          firstSeenAt: attemptTime,
          buckets: new Map<number, Bucket>(),
        };

        if (existing.firstSeenAt == null || attemptTime < existing.firstSeenAt) {
          existing.firstSeenAt = attemptTime;
        }

        if (!existing.label && label) {
          existing.label = label;
        }

        const bucket = existing.buckets.get(weekStart) || { sum: 0, count: 0 };
        bucket.sum += numericGrade;
        bucket.count += 1;
        existing.buckets.set(weekStart, bucket);
        aggregates.set(conceptKey, existing);
      });
    });

    const sortedDatasets: ConceptPerformanceDataset[] = Array.from(aggregates.entries())
      .map(([conceptKey, aggregate]) => ({
        conceptKey,
        conceptLabel: aggregate.label || conceptKey,
        firstSeenAt: aggregate.firstSeenAt ?? null,
        points: Array.from(aggregate.buckets.entries())
          .sort((a, b) => a[0] - b[0])
          .map(([weekStart, stats]) => ({
            weekStart,
            weekLabel: formatWeekRangeLabel(weekStart),
            averageGrade: Number((stats.sum / stats.count).toFixed(2)),
            attemptCount: stats.count,
          })),
      }))
      .sort((a, b) => a.conceptLabel.localeCompare(b.conceptLabel));

    const datasets: Record<string, ConceptPerformanceDataset> = {};
    const options = sortedDatasets.map((dataset) => {
      datasets[dataset.conceptKey] = dataset;
      return {
        value: dataset.conceptKey,
        label: dataset.conceptLabel,
        firstSeenAt: dataset.firstSeenAt,
      };
    });

    return { options, datasets };
  }, [sessions, questionConcepts]);

  const conceptDataStatus = useMemo(() => {
    if (!sessions.length) {
      return { hasPending: false, errors: 0 };
    }
    let pending = 0;
    let errors = 0;
    sessions.forEach((session) => {
      const trimmedQuestionId = session.questionId?.trim();
      if (!trimmedQuestionId) return;
      const conceptState = questionConcepts[trimmedQuestionId];
      if (!conceptState || conceptState.status === 'loading') {
        pending += 1;
      } else if (conceptState.status === 'error') {
        errors += 1;
      }
    });
    return { hasPending: pending > 0, errors };
  }, [sessions, questionConcepts]);

  const filteredSessions = useMemo(() => {
    let result = sessions;

    // Filter by search query (attemptId or socketId/socketIds)
    if (searchQuery.trim()) {
      const query = searchQuery.trim().toLowerCase();
      result = result.filter((session) => {
        const attemptIdMatch = session.attemptId?.toLowerCase().includes(query);

        // Search through all socketIds if available
        const socketIdMatch = session.socketIds
          ? session.socketIds.some((id) => id.toLowerCase().includes(query))
          : session.socketId?.toLowerCase().includes(query);

        return attemptIdMatch || socketIdMatch;
      });
    }

    // Filter by selected concepts
    if (selectedConcepts.length > 0) {
      const selectedSet = new Set(selectedConcepts);
      result = result.filter((session) => {
        const trimmedQuestionId = session.questionId?.trim();
        if (!trimmedQuestionId) return false;
        const conceptState = questionConcepts[trimmedQuestionId];
        if (!conceptState || conceptState.status !== 'success' || !conceptState.concepts?.length) {
          return false;
        }
        return conceptState.concepts.some((concept) => selectedSet.has(buildConceptKey(concept)));
      });
    }

    return result;
  }, [sessions, selectedConcepts, questionConcepts, searchQuery]);

  useEffect(() => {
    setSelectedConcepts((current) => {
      if (!conceptPerformance.options.length) {
        return current.length ? [] : current;
      }
      const validSelections = current.filter((value) => Boolean(conceptPerformance.datasets[value]));
      if (validSelections.length !== current.length) {
        return validSelections;
      }
      return current;
    });
  }, [conceptPerformance]);

  const selectedDatasets = useMemo(() => {
    if (!selectedConcepts.length) return [];
    return selectedConcepts
      .map((conceptKey) => conceptPerformance.datasets[conceptKey])
      .filter((dataset): dataset is ConceptPerformanceDataset => Boolean(dataset));
  }, [selectedConcepts, conceptPerformance]);

  const conceptChartData = useMemo(() => {
    if (!selectedDatasets.length) {
      return null;
    }
    const weekStarts = Array.from(
      new Set(
        selectedDatasets.flatMap((dataset) => dataset.points.map((point) => point.weekStart)),
      ),
    ).sort((a, b) => a - b);
    if (!weekStarts.length) {
      return null;
    }
    const labels = weekStarts.map((weekStart) => formatWeekRangeLabel(weekStart));
    const datasets = selectedDatasets.map((dataset, index) => {
      const paletteEntry = CONCEPT_COLOR_PALETTE[index % CONCEPT_COLOR_PALETTE.length];
      const pointMap = new Map(dataset.points.map((point) => [point.weekStart, point] as const));
      const attemptCounts = weekStarts.map((weekStart) => pointMap.get(weekStart)?.attemptCount ?? 0);
      const data = weekStarts.map((weekStart) => pointMap.get(weekStart)?.averageGrade ?? null);
      const enrichedDataset = {
        label: dataset.conceptLabel,
        data,
        borderColor: paletteEntry.border,
        backgroundColor: paletteEntry.fill,
        tension: 0.3,
        fill: false,
        pointRadius: 4,
        spanGaps: true,
      } as ChartDataset<'line'> & { customAttemptCounts: number[] };
      enrichedDataset.customAttemptCounts = attemptCounts;
      return enrichedDataset;
    });
    return { labels, datasets };
  }, [selectedDatasets]);

  const conceptChartOptions = useMemo<ChartOptions<'line'>>(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: { intersect: false, mode: 'index' as const },
    plugins: {
      legend: { display: selectedDatasets.length > 1 },
      tooltip: {
        callbacks: {
          afterLabel: (context) => {
            const attempts = (context.dataset as ChartDataset<'line'> & { customAttemptCounts?: number[] })
              ?.customAttemptCounts?.[context.dataIndex];
            if (typeof attempts === 'number' && attempts > 0) {
              return `Attempts: ${attempts}`;
            }
            return undefined;
          },
        },
      },
    },
    scales: {
      x: {
        ticks: { maxRotation: 0, minRotation: 0, autoSkip: true },
        grid: { display: false },
      },
      y: {
        beginAtZero: true,
        title: { display: true, text: 'Average grade' },
      },
    },
  }), [selectedDatasets]);

  const conceptSummaries = useMemo(
    () =>
      selectedDatasets.map((dataset, index) => {
        const attempts = dataset.points.reduce((sum, point) => sum + point.attemptCount, 0);
        const paletteEntry = CONCEPT_COLOR_PALETTE[index % CONCEPT_COLOR_PALETTE.length];
        return {
          conceptKey: dataset.conceptKey,
          label: dataset.conceptLabel,
          weeks: dataset.points.length,
          attempts,
          color: paletteEntry.border,
        };
      }),
    [selectedDatasets],
  );

  const totalPages = totalAttempts > 0 ? Math.ceil(totalAttempts / PAGE_SIZE) : 1;

  const attemptsCountLabel = totalAttempts > 0
    ? (sessions.length === filteredSessions.length
        ? `${filteredSessions.length} of ${totalAttempts} attempts (page ${currentPage}/${totalPages})`
        : `${filteredSessions.length} filtered, ${totalAttempts} total`)
    : (sessions.length === filteredSessions.length
        ? `${sessions.length} attempts`
        : `${filteredSessions.length} of ${sessions.length} attempts`);

  const handleImageMouseEnter = useCallback((url: string, event: React.MouseEvent<HTMLImageElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setZoomedImage({
      url,
      x: rect.right + 10,
      y: rect.top,
    });
  }, []);

  const handleImageMouseLeave = useCallback(() => {
    setZoomedImage(null);
  }, []);

  const handleImageMouseMove = useCallback((event: React.MouseEvent<HTMLImageElement>) => {
    if (!zoomedImage) return;
    const rect = event.currentTarget.getBoundingClientRect();
    setZoomedImage(prev => prev ? {
      ...prev,
      x: rect.right + 10,
      y: rect.top,
    } : null);
  }, [zoomedImage]);

  return (
    <div className="cost-dashboard user-attempts-page" style={{ background: 'transparent' }}>
      <div className="dashboard-content">
        <div className="table-container user-attempts-container">
          <div className="user-attempts-header">
            <div className="user-attempts-header-left">
              <button type="button" className="filter-btn" onClick={handleBackClick}>
                ← Back
              </button>
              <div>
                <h3>User Attempts</h3>
                <div style={{ fontSize: 13, color: '#4b5563' }}>
                  {userId ? `User ID: ${userId}` : 'Provide a userId query parameter to load attempts.'}
                </div>
              </div>
            </div>
            <div className="user-attempts-controls">
              <span className="env-pill">
                {environment === 'local' ? '🟡 Local' : environment === 'staging' ? '🔵 Staging' : '🟢 Production'}
              </span>
              <select
                className="usage-org-select"
                value={environment}
                onChange={(e) => handleEnvironmentChange(e.target.value as 'local' | 'staging' | 'production')}
              >
                <option value="local">Local</option>
                <option value="staging">Staging</option>
                <option value="production">Production</option>
              </select>
            </div>
          </div>

          {!userId && (
            <div className="usage-empty-state">
              Missing userId. Append <code>?userId=&lt;USER_ID&gt;</code> to the URL to view attempts.
            </div>
          )}

          {userId && (
            <>
              <div className="user-summary-card">
                {userLoading ? (
                  <div className="dashboard-loading">Loading user details...</div>
                ) : user ? (
                  <>
                    <div className="user-summary-main">
                      <div>
                        <div className="user-summary-name">{user.name || user.email || 'Unnamed user'}</div>
                        <div className="user-summary-email">{user.email || 'No email stored'}</div>
                      </div>
                      <div className="user-summary-meta">
                        <span>Role: {user.role || '—'}</span>
                        <span>Grade: {user.grade || '—'}</span>
                        <span>Status: {user.entityState || '—'}</span>
                      </div>
                    </div>
                    <div className="user-summary-dates">
                      Joined platform: {formatTimestamp(toMs(user.createdAt))}
                    </div>
                  </>
                ) : (
                  <div className="usage-empty-state">User not found.</div>
                )}
                {userError && <div className="dashboard-error">{userError}</div>}
              </div>

              <div className="usage-section concept-progress-section">
                <div className="usage-section-header">
                  <h4>Concept Performance</h4>
                  {conceptPerformance.options.length > 0 && (
                    <div className="concept-progress-select-wrapper">
                      <select
                        multiple
                        size={Math.min(6, conceptPerformance.options.length)}
                        className="concept-progress-select"
                        value={selectedConcepts}
                        onChange={handleConceptSelectChange}
                      >
                        {conceptPerformance.options.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <div className="concept-progress-select-hint">Hold Cmd/Ctrl or Shift to pick multiple concepts.</div>
                    </div>
                  )}
                </div>
                {!sessions.length ? (
                  <div className="usage-empty-state">
                    Attempts are required to build the concept report.
                  </div>
                ) : conceptPerformance.options.length === 0 ? (
                  <div className="usage-empty-state">
                    {conceptDataStatus.hasPending
                      ? 'Collecting concept tags… The report appears after the question concepts finish loading.'
                      : conceptDataStatus.errors > 0
                        ? 'Failed to load some concept tags. Refresh attempts to retry fetching concepts.'
                        : 'No concept-level grades detected for this user yet.'}
                  </div>
                ) : !selectedDatasets.length ? (
                  <div className="usage-empty-state">Select at least one concept to see weekly performance.</div>
                ) : (
                  <div className="concept-progress-card">
                    <div className="concept-progress-meta">
                      <div>
                        <div className="concept-progress-label">Tracking concepts</div>
                        <div className="concept-progress-name">
                          {selectedDatasets.length === 1
                            ? selectedDatasets[0].conceptLabel
                            : `${selectedDatasets.length} concepts selected`}
                        </div>
                        {selectedDatasets.length === 1 && selectedDatasets[0].firstSeenAt ? (
                          <div className="concept-progress-range">
                            Tracking since {formatTimestamp(selectedDatasets[0].firstSeenAt)}
                          </div>
                        ) : (
                          <div className="concept-progress-hint">Compare week-on-week grades across chosen concepts.</div>
                        )}
                      </div>
                      <div className="concept-progress-stats">
                        {conceptSummaries.map((summary) => (
                          <div key={summary.conceptKey} className="concept-progress-stat-row">
                            <span className="concept-progress-color" style={{ background: summary.color }} />
                            <div>
                              <div className="concept-progress-stat-label">{summary.label}</div>
                              <div className="concept-progress-stat-meta">
                                Weeks: {summary.weeks} · Attempts: {summary.attempts}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    {conceptChartData ? (
                      <div className="concept-progress-chart">
                        <Line data={conceptChartData} options={conceptChartOptions} />
                      </div>
                    ) : (
                      <div className="concept-progress-placeholder">
                        Need at least one graded week to plot a trend.
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="usage-section">
                <div className="usage-section-header">
                  <h4>Attempts ({attemptsCountLabel})</h4>
                  <div className="usage-section-actions">
                    <input
                      type="text"
                      placeholder="Search by Attempt ID or Socket ID..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="usage-org-select"
                      style={{ minWidth: '280px', marginRight: '8px' }}
                    />
                    <button
                      type="button"
                      className="filter-btn"
                      onClick={handleRefreshSessions}
                      disabled={sessionsLoading}
                    >
                      {sessionsLoading ? 'Refreshing...' : 'Refresh Attempts'}
                    </button>
                    <button
                      type="button"
                      className="filter-btn"
                      onClick={handleRefreshAttemptImages}
                      disabled={isRefreshingImages || sessionsLoading}
                    >
                      {isRefreshingImages ? 'Refreshing Images...' : 'Refresh Images'}
                    </button>
                    <button
                      type="button"
                      className="filter-btn"
                      onClick={handleExportCsv}
                      disabled={sessionsLoading || !sessions.length}
                    >
                      Export CSV
                    </button>
                    {imageRefreshStatus && (
                      <span
                        className={`image-refresh-status${
                          imageRefreshStatus.tone === 'success'
                            ? ' is-success'
                            : imageRefreshStatus.tone === 'error'
                              ? ' is-error'
                              : ''
                        }`}
                        role="status"
                        aria-live="polite"
                      >
                        {imageRefreshStatus.message}
                      </span>
                    )}
                  </div>
                </div>
                {sessionsError && <div className="dashboard-error">{sessionsError}</div>}
                {sessionsLoading && <div className="dashboard-loading">Loading attempts...</div>}
                {!sessionsLoading && sessions.length === 0 && !sessionsError && (
                  <div className="usage-empty-state">No attempts found for this user.</div>
                )}
                {!sessionsLoading &&
                  sessions.length > 0 &&
                  filteredSessions.length === 0 &&
                  !sessionsError && (
                    <div className="usage-empty-state">
                      No attempts match the selected concepts yet.{` `}
                      {conceptDataStatus.hasPending
                        ? 'Concept tags are still loading for some questions.'
                        : 'Try choosing different concepts or refresh the attempts list.'}
                    </div>
                  )}
                {!sessionsLoading && totalPages > 1 && (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '12px 0' }}>
                    <button
                      type="button"
                      className="filter-btn"
                      disabled={currentPage <= 1}
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    >
                      Previous
                    </button>
                    <span style={{ fontSize: 13, color: '#4b5563' }}>
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      type="button"
                      className="filter-btn"
                      disabled={currentPage >= totalPages}
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    >
                      Next
                    </button>
                  </div>
                )}
                {!sessionsLoading && filteredSessions.length > 0 && (
                  <div className="usage-attempts-grid">
                    {filteredSessions.map((session) => {
                      const fermiAnalysis = session?.attemptDetails?.fermiAnalysis || null;
                      const gradeLookup = buildConceptGradeLookup(fermiAnalysis?.conceptGrades);
                      const formattedOverallGrade = formatConceptGrade(fermiAnalysis?.overallGrade);
                      const hasOverallGrade = formattedOverallGrade !== '-';
                      return (
                        <div key={session.attemptId} className="attempt-card">
                          <div className="attempt-id-row">
                            <button
                              type="button"
                              className="session-id"
                              onClick={() => openSessionLogs(session.attemptId)}
                            >
                              {session.attemptId}
                            </button>
                          </div>
                          {session.socketIds && session.socketIds.length > 0 && (
                            <div className="attempt-meta-row" style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>
                              <span className="attempt-meta-label">Socket ID{session.socketIds.length > 1 ? 's' : ''}:</span>{' '}
                              <span className="attempt-meta-value" style={{ fontFamily: 'monospace', fontSize: 11 }}>
                                {session.socketIds.length === 1 ? (
                                  session.socketIds[0]
                                ) : (
                                  <>
                                    <strong style={{ color: '#059669' }}>{session.socketIds[session.socketIds.length - 1]}</strong>
                                    {' '}(latest)
                                    <details style={{ display: 'inline', marginLeft: '8px' }}>
                                      <summary style={{ display: 'inline', cursor: 'pointer', color: '#2563eb' }}>
                                        +{session.socketIds.length - 1} more
                                      </summary>
                                      <div style={{ marginTop: '4px', paddingLeft: '16px', color: '#6b7280' }}>
                                        {session.socketIds.slice(0, -1).map((id, idx) => (
                                          <div key={idx} style={{ marginBottom: '2px' }}>
                                            {idx + 1}. {id}
                                          </div>
                                        ))}
                                      </div>
                                    </details>
                                  </>
                                )}
                              </span>
                            </div>
                          )}
                          <div className="attempt-meta-row" style={{ fontSize: 12, color: '#4b5563', marginBottom: 4 }}>
                            <span className="attempt-meta-label">First attempted:</span>{' '}
                            <span className="attempt-meta-value">{formatTimestamp(session.attemptCreatedAt)}</span>
                          </div>
                          <div className="attempt-meta-row" style={{ fontSize: 12, color: '#4b5563', marginBottom: 8 }}>
                            <span className="attempt-meta-label">Last attempted:</span>{' '}
                            <span className="attempt-meta-value">{formatTimestamp(session.attemptUpdatedAt || session.lastMessageTime)}</span>
                          </div>
                          <div className="attempt-images">
                            <div className="attempt-image-block">
                              <div className="attempt-image-label">Question</div>
                              {session.questionUrl ? (
                                <img
                                  src={session.questionUrl}
                                  alt="Question"
                                  className="attempt-image"
                                  loading="lazy"
                                  onMouseEnter={(e) => handleImageMouseEnter(session.questionUrl!, e)}
                                  onMouseLeave={handleImageMouseLeave}
                                  onMouseMove={handleImageMouseMove}
                                  style={{ cursor: 'zoom-in' }}
                                />
                              ) : (
                                <div className="attempt-image-placeholder">No question image</div>
                              )}
                            </div>
                            <div className="attempt-image-block">
                              <div className="attempt-image-label">Solution</div>
                              {session.solutionUrl ? (
                                <img
                                  src={session.solutionUrl}
                                  alt="Solution"
                                  className="attempt-image"
                                  loading="lazy"
                                  onMouseEnter={(e) => handleImageMouseEnter(session.solutionUrl!, e)}
                                  onMouseLeave={handleImageMouseLeave}
                                  onMouseMove={handleImageMouseMove}
                                  style={{ cursor: 'zoom-in' }}
                                />
                              ) : (
                                <div className="attempt-image-placeholder">No solution image</div>
                              )}
                            </div>
                          </div>
                          <div className="attempt-concepts">
                            <div className="attempt-concepts-label">Concept Tags</div>
                            {hasOverallGrade && (
                              <div className="attempt-overall-grade" style={{ fontSize: 13, color: '#374151', marginBottom: 6 }}>
                                Overall grade: <strong>{formattedOverallGrade}</strong>
                              </div>
                            )}
                            {(() => {
                              const trimmedQuestionId = session.questionId?.trim();
                              if (!trimmedQuestionId) {
                                return <div className="concept-tag-placeholder">No question ID available.</div>;
                              }
                              const conceptState = questionConcepts[trimmedQuestionId];
                              if (!conceptState || conceptState.status === 'loading') {
                                return <div className="concept-tag-placeholder">Loading concept tags...</div>;
                              }
                              if (conceptState.status === 'error') {
                                return (
                                  <div className="concept-tag-placeholder">
                                    {conceptState.error || 'Failed to load concept tags.'}
                                  </div>
                                );
                              }
                              if (!conceptState.concepts || conceptState.concepts.length === 0) {
                                return <div className="concept-tag-placeholder">No concept tags found.</div>;
                              }
                              return (
                                <table className="concept-tag-table">
                                  <thead>
                                    <tr>
                                      <th>Concept</th>
                                      <th>Grade</th>
                                      <th>Depth</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {conceptState.concepts.map((concept, index) => {
                                      const gradeValue = getConceptGradeValue(concept, gradeLookup);
                                      return (
                                        <tr key={concept.topicId || `${concept.topicName}-${index}`}>
                                          <td>{concept.topicName}</td>
                                          <td className="concept-grade-cell">{formatConceptGrade(gradeValue)}</td>
                                          <td className="concept-depth-cell">
                                            {typeof concept.depthRequired === 'number' ? concept.depthRequired : '—'}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              );
                            })()}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {!sessionsLoading && totalPages > 1 && (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '12px 0' }}>
                    <button
                      type="button"
                      className="filter-btn"
                      disabled={currentPage <= 1}
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    >
                      Previous
                    </button>
                    <span style={{ fontSize: 13, color: '#4b5563' }}>
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      type="button"
                      className="filter-btn"
                      disabled={currentPage >= totalPages}
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      
      {zoomedImage && (
        <div
          className="image-zoom-overlay"
          style={{
            position: 'fixed',
            left: `${zoomedImage.x}px`,
            top: `${zoomedImage.y}px`,
            zIndex: 9999,
            pointerEvents: 'none',
            maxWidth: '600px',
            maxHeight: '80vh',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
            border: '2px solid #e5e7eb',
            borderRadius: '8px',
            backgroundColor: 'white',
            padding: '8px',
          }}
        >
          <img
            src={zoomedImage.url}
            alt="Zoomed"
            style={{
              width: '100%',
              height: 'auto',
              display: 'block',
              borderRadius: '4px',
            }}
          />
        </div>
      )}
    </div>
  );
};

export default UserAttemptsPage;